import { InstructionType } from 'app/src/engine/Instruction';
import InstructionFactory from 'app/src/engine/instructions/InstructionFactory';
import GameStateStore, { TurnPhase } from 'app/src/game/GameStateStore';
import Logger from 'app/src/Logger';
import useValueChanged from 'app/src/utils/useValueChanged';
import { useEffect } from 'react';

export function executeNextStep(gameStateStore: GameStateStore, program: InstructionType[]): void {
	switch (gameStateStore.currentTurnPhase) {
		case TurnPhase.GameStart:
			Logger.log('Executing TurnPhase.GameStart');

			gameStateStore.advanceToNextPhase();
			break;

		case TurnPhase.LoadInstructions:
			Logger.log('------- NEW TURN -------');
			Logger.log('Executing TurnPhase.LoadInstructions');

			if (gameStateStore.currentTurn < program.length) {
				const instruction = InstructionFactory.getInstruction(program[gameStateStore.currentTurn]);
				gameStateStore.setInstructionForRobot(instruction);
			}

			if (gameStateStore.numberOfRobotsWithPendingInstructions > 0) {
				gameStateStore.advanceToNextPhase();
			} else {
				gameStateStore.finishGameForNoInstructions();
			}
			break;

		case TurnPhase.ExecuteInstructions:
			Logger.log('Executing TurnPhase.ExecuteInstructions');

			// Execute next robots' instructions.
			if (gameStateStore.numberOfRobotsWithPendingInstructions > 0) {
				gameStateStore.executeNextInstruction();
			} else {
				if (!gameStateStore.gameIsFinished) {
					gameStateStore.advanceToNextPhase();
				}
			}
			break;

		case TurnPhase.ExecuteTileInteractions:
			Logger.log('Executing TurnPhase.ExecuteTileInteractions');

			// Execute next plugin instruction.
			if (gameStateStore.numberOfPendingTileInteractions > 0) {
				gameStateStore.executeNextTileInteraction();
			} else {
				if (!gameStateStore.gameIsFinished) {
					gameStateStore.advanceToNextPhase();
				}
			}
			break;
	}
}

export default function useLevelSequencer(
	gameStateStore: GameStateStore,
	program: InstructionType[],
): void {
	// Track what properties have chanced since last render.
	const turnPhaseChanged = useValueChanged(gameStateStore.currentTurnPhase);
	const currentTileInteractionChanged = useValueChanged(gameStateStore.currentTileInteraction);
	const currentActionResultIndexChanged = useValueChanged(gameStateStore.currentActionResultIndex);
	const currentRobotExecutionChanged = useValueChanged(gameStateStore.currentRobotExecution);

	// If the gameStateStore has advanced to the next turn phase, we need to
	// execute what's needed for that phase. Most of the time this just means
	// passing through to the next step or doing syncronous tasks such as setting
	// the robot's next instruction, but sometimes this means kicking off some
	// async animations. When those animations complete, changes in gameStateStore
	// will trigger other effects to advance the game state (see below).
	useEffect(() => {
		if (!gameStateStore.gameIsFinished && turnPhaseChanged) {
			Logger.log(
				`Calling to execute next step. [${TurnPhase[turnPhaseChanged.previous]} → ${
					TurnPhase[gameStateStore.currentTurnPhase]
				}]`,
			);
			executeNextStep(gameStateStore, program);
		}
	}, [program, gameStateStore, turnPhaseChanged]);

	// There's a special case where a robot instruction or tile interaction has
	// been executed and neither resulted in new action results. In these cases,
	// we need to move on to the next step (possibly the next phase).
	useEffect(() => {
		if (
			!gameStateStore.gameIsFinished &&
			((currentRobotExecutionChanged &&
				gameStateStore.currentRobotExecution > 0 &&
				gameStateStore.currentTurnPhase === TurnPhase.ExecuteInstructions) ||
				(currentTileInteractionChanged &&
					gameStateStore.currentTileInteraction > 0 &&
					gameStateStore.currentTurnPhase === TurnPhase.ExecuteTileInteractions)) &&
			gameStateStore.currentActionResultIndex === null
		) {
			Logger.log(
				`No animations to execute in phase ${
					TurnPhase[gameStateStore.currentTurnPhase]
				}. Moving to next step.`,
			);
			executeNextStep(gameStateStore, program);
		}
	}, [currentRobotExecutionChanged, currentTileInteractionChanged, gameStateStore, program]);

	// When all animations in a set are completed via completeAnimation,
	// currentActionResultIndex will increase. At some point,
	// currentActionResultIndex will run off the end of actionResults which
	// signifies that all animation sets have been executed. At this point, we
	// need to move on to the next step. Note: this will happen both for robot and
	// tile interactions.
	useEffect(() => {
		if (
			!gameStateStore.gameIsFinished &&
			currentActionResultIndexChanged &&
			gameStateStore.currentActionResultIndex === gameStateStore.actionResults.length
		) {
			Logger.log(
				`All action results sets completed animating in phase ${
					TurnPhase[gameStateStore.currentTurnPhase]
				}. Moving to next step.`,
			);
			executeNextStep(gameStateStore, program);
		}
	}, [
		currentActionResultIndexChanged,
		gameStateStore,
		gameStateStore.actionResults.length,
		gameStateStore.currentActionResultIndex,
		program,
	]);
}
